import React from 'react';
import { exportListeningTxt } from '../../../utils/listeningExport';
import { ListeningQuestion } from '../../../config/api';

interface Props {
  score: any;
  results: any[];
  transcript?: string;
  audioUrl?: string;
  audioName?: string;
  questions: ListeningQuestion[];
  onBack: () => void;
  /** Back to the question page for a retake with the same audio/questions. */
  onRetry: () => void;
}

const ListeningScore: React.FC<Props> = ({ score, results, transcript, audioUrl, audioName = 'listening-practice', questions, onBack, onRetry }) => {
  const handleExport = () => {
    // Rebuild the id -> user answer map from the graded results so the export
    // records what the user actually answered (blank answers included).
    const userAnswers: Record<string, string> = {};
    results.forEach((r) => {
      userAnswers[String(r.id)] = r.user_answer ?? '';
    });
    exportListeningTxt({ audioName, transcript: transcript || '', questions, userAnswers });
  };

  return (
    <div className="listening-score">
      <h3 className="section-title">4. Results</h3>
      <p style={{ marginBottom: '16px' }}>
        Correct <strong>{score.correct}</strong> / {score.total_questions} &nbsp;
        (Accuracy: {score.accuracy}%)
      </p>
      
      {audioUrl && (
        <div className="audio-section" style={{ marginBottom: '24px' }}>
          <h4 className="section-subtitle">Audio</h4>
          <audio controls src={audioUrl} style={{ width: '100%' }} />
        </div>
      )}
      
      <div className="results-container" style={{ overflowX: 'auto' }}>
        <table className="results-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid var(--border-light)', padding: '8px' }}>#</th>
              <th style={{ border: '1px solid var(--border-light)', padding: '8px' }}>Question</th>
              <th style={{ border: '1px solid var(--border-light)', padding: '8px' }}>Your Answer</th>
              <th style={{ border: '1px solid var(--border-light)', padding: '8px' }}>Correct Answer</th>
              <th style={{ border: '1px solid var(--border-light)', padding: '8px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, idx) => (
              <tr key={r.id}>
                <td style={{ border: '1px solid var(--border-light)', padding: '8px' }}>{idx + 1}</td>
                <td style={{ border: '1px solid var(--border-light)', padding: '8px' }}>{r.question}</td>
                <td style={{ border: '1px solid var(--border-light)', padding: '8px' }}>{r.user_answer}</td>
                <td style={{ border: '1px solid var(--border-light)', padding: '8px' }}>{r.correct_answer}</td>
                <td style={{ border: '1px solid var(--border-light)', padding: '8px' }}>{r.is_correct ? '✅' : '❌'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {transcript && (
        <div className="transcript-section" style={{ marginTop: '32px' }}>
          <h4 className="section-subtitle">Transcript Reference</h4>
          <div className="transcript-content" style={{ 
            backgroundColor: 'var(--background-light)', 
            padding: '16px', 
            borderRadius: '8px',
            border: '1px solid var(--border-light)',
            maxHeight: '300px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {transcript}
          </div>
        </div>
      )}
      
      <div className="result-actions" style={{ marginTop: '24px' }}>
        <button className="btn btn-secondary" onClick={onRetry}>← Back to Questions</button>
        <button className="btn btn-secondary" onClick={handleExport}>Export TXT</button>
        <button className="btn btn-primary" onClick={onBack}>Back to Modules</button>
      </div>
    </div>
  );
};

export default ListeningScore; 